// CategoryIdListType.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalContainer from '../component/UniversalContainer.jsx';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  CategoryI18nKey,
  CATEGORY_FEED_REMOTE_CONFIG_KEYS,
} from '../resource/categoryConstants.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';
import { generateCollectionPageJsonLd } from '../resource/generateCollectionPageJsonLd.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({ context, params, request }) => {
  const log = legacySSRLog.extend('CategoryIdListType loader()');
  const errorLog = legacySSRError.extend('CategoryIdListType loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get category feed from remote config based on listType
    let state = store.getState();
    const { categoryId, listType } = params;

    const feedNameRemoteConfigKey = CATEGORY_FEED_REMOTE_CONFIG_KEYS[listType];
    if (!feedNameRemoteConfigKey) {
      // Invalid listType, return noindex
      return {
        data: { store: {} },
        params,
        meta: [{ name: 'robots', content: 'noindex, nofollow' }],
        staticContext: {
          requestHeaders: {
            hostname: request?.headers?.get('host') || 'swag.live',
          },
        },
      };
    }

    const categoryFeedName = getRemoteConfigData(
      state,
      feedNameRemoteConfigKey,
      undefined,
      {
        CATEGORY: categoryId,
      }
    );

    // Fetch category feed count
    let feedCount = 0;
    if (categoryFeedName) {
      feedCount = await fetchFeedTotalCount({
        feedNameWithQuery: categoryFeedName,
        httpProxyHeaders,
      }).catch(() => 0);
    }

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, feedCount });

    // Get i18n instance
    const { i18n } = getI18nInstance();
    const { t } = i18n;

    // Get category name and list type name from translation
    const categoryName = t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
    });
    const categoryListType = CategoryI18nKey[listType];

    // Prepare title and description
    const title = t(`seo_category_${categoryListType}_title`, {
      ns: TranslationNamespace.SEO,
      category: categoryName,
    });
    const description =
      t(`seo_category_${categoryId}_${categoryListType}_description`, {
        ns: TranslationNamespace.SEO,
      }) ||
      t(`seo_category_${categoryListType}_description`, {
        ns: TranslationNamespace.SEO,
        category: categoryName,
      });

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const canonicalPath = `/category/${categoryId}/${listType}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;
    const jsonLd = await generateCollectionPageJsonLd({
      origin,
      categoryId,
      listType,
      state,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [categoryName, categoryListType, 'SWAG', 'category'],
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      jsonLd,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const CategoryIdRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'CategoryIdListType',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalContainer filename="MessageCategoryTabContent" />
    </>
  );
};

export default CategoryIdRoute;
